import { Signal } from "@lit-labs/signals";
import { DEFAULT_COMMUNITY_LANGUAGE } from "api-contracts/rpc/community";
import type { CreatorItem } from "ui-common/services/creator-select/types";
import type {
  IWritableCommunityFormState,
  ICommunityFormState,
  CommunityFormData,
  CommunityFormErrors,
  CommunityUIToggles,
} from "./types";

const DEFAULT_FORM_DATA: CommunityFormData = {
  title: "",
  description: "",
  language: DEFAULT_COMMUNITY_LANGUAGE,
  ctaText: "",
  showMemberCount: true,
};

const DEFAULT_UI_TOGGLES: CommunityUIToggles = {
  showLinkedEvents: false,
  showLinkedLinks: false,
  showCustomCta: false,
  showCoPromotion: false,
};

export class CommunityFormState implements IWritableCommunityFormState {
  readonly formData = new Signal.State<CommunityFormData>({ ...DEFAULT_FORM_DATA });
  readonly validationErrors = new Signal.State<CommunityFormErrors>({});
  readonly isSubmitting = new Signal.State<boolean>(false);
  readonly isLoading = new Signal.State<boolean>(false);
  readonly hasUnsavedChanges = new Signal.State<boolean>(false);
  readonly communityId = new Signal.State<string | undefined>(undefined);
  readonly communityStatus = new Signal.State<string | undefined>(undefined);
  readonly thumbnailFileId = new Signal.State<string | undefined>(undefined);
  readonly thumbnailUrl = new Signal.State<string | undefined>(undefined);
  readonly uiToggles = new Signal.State<CommunityUIToggles>({ ...DEFAULT_UI_TOGGLES });
  readonly linkedEventIds = new Signal.State<string[]>([]);
  readonly collaboratorHandles = new Signal.State<string[]>([]);
  readonly existingCollaborators = new Signal.State<CreatorItem[]>([]);
  readonly availableEvents = new Signal.State<Array<{ value: string; label: string }>>([]);

  reset(): void {
    this.formData.set({ ...DEFAULT_FORM_DATA });
    this.validationErrors.set({});
    this.isSubmitting.set(false);
    this.isLoading.set(false);
    this.hasUnsavedChanges.set(false);
    this.communityId.set(undefined);
    this.communityStatus.set(undefined);
    this.thumbnailFileId.set(undefined);
    this.thumbnailUrl.set(undefined);
    this.uiToggles.set({ ...DEFAULT_UI_TOGGLES });
    this.linkedEventIds.set([]);
    this.collaboratorHandles.set([]);
    this.existingCollaborators.set([]);
    this.availableEvents.set([]);
  }
}

export function asReadonlyCommunityFormState(state: CommunityFormState): ICommunityFormState {
  return state as ICommunityFormState;
}
